import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/apiService';
import { Helmet } from 'react-helmet';
import Select from 'react-select';

function formatDateToGerman(dateString, withTime = false) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date)) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  if (!withTime) {
    return `${day}.${month}.${year}`;
  } else {
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hour}:${minute}`;
  }
}

function formatDuration(enteredAt, leftAt) {
  if (!enteredAt || !leftAt) return '-';
  const start = new Date(enteredAt);
  const end = new Date(leftAt);
  let diff = Math.abs(end - start); // in ms

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  diff -= days * 1000 * 60 * 60 * 24;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  diff -= hours * 1000 * 60 * 60;
  const minutes = Math.floor(diff / (1000 * 60));

  let parts = [];
  if (days) parts.push(`${days} Tag${days > 1 ? 'e' : ''}`);
  if (hours) parts.push(`${hours} Std.`);
  if (minutes) parts.push(`${minutes} Min.`);
  if (!parts.length) parts.push('<1 Min.');

  return parts.join(', ');
}

const StageHistoryForm = ({ pipelineInstId }) => {
  const [histories, setHistories] = useState([]);
  const [instances, setInstances] = useState([]);
  const [stages, setStages] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
     const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 25; // oder dein Standardwert



  // Filter-States (jetzt mit Objekt für react-select)
  const [filterInstance, setFilterInstance] = useState(null);
  const [filterCustomer, setFilterCustomer] = useState(null);
  const [filterStage, setFilterStage] = useState(null);
  const [filterPipeline, setFilterPipeline] = useState(null);
  const [filterResponsible, setFilterResponsible] = useState(null);
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');


  // --- Stammdaten laden (nur einmal) ---
  useEffect(() => {
    const fetchBasics = async () => {
      try {
        const [insts, pipes, allStages] = await Promise.all([
          api.get('/pipeline-instances'),
          api.get('/pipeline-definitions'),
          api.get('/pipeline-stages')
        ]);
        setInstances(Array.isArray(insts.data) ? insts.data : insts.data.rows || []);
        setPipelines(pipes.data);
        setStages(allStages.data.sort((a, b) => a.order - b.order));
      } catch (err) {
        setError('Fehler beim Laden der Stammdaten.');
      }
    };
    fetchBasics();
  }, []);

  // --- Filteroptionen vorbereiten ---
// NEUE VERSION (Duplikate vermeiden, Name als Label, ID als Value)
const uniqueNames = Array.from(
  new Set(instances.map(i => i.name?.trim() || `#${i.id}`))
);

const instanceOptions = uniqueNames.map(name => ({
  value: name,
  label: name
}));

  const customerOptions = [
    ...new Map(
      instances
        .filter(i => i.customer || i.prospect)
        .map(i => {
          const account = i.customer || i.prospect;
          const type = i.customer ? 'customer' : 'prospect';
          return [`${type}-${account.id}`, { ...account, crmAccountKey: `${type}-${account.id}` }];
        })
    ).values()
  ]
    .sort((a, b) => (a.company || a.name || '').localeCompare(b.company || b.name || ''))
    .map(c => ({
      value: c.crmAccountKey,
      label: c.company || c.name
    }));

  const stageOptions = stages.map(s => ({
    value: s.id,
    label: s.name
  }));

  const pipelineOptions = pipelines.map(p => ({
    value: p.id,
    label: p.name
  }));

  // Verantwortlicher-Optionen
  const responsibleOptions = [
    ...new Map(
      instances
        .filter(i => i.responsible)
        .map(i => [i.responsible.id, i.responsible])
    ).values()
  ]
    .sort((a, b) =>
      ((a.firstName || '') + ' ' + (a.lastName || '')).localeCompare(
        (b.firstName || '') + ' ' + (b.lastName || '')
      )
    )
    .map(r => ({
      value: r.id,
      label: `${r.firstName || ''} ${r.lastName || ''}`.trim() || `#${r.id}`
    }));

  // --- Filterfunktion zum Nachladen ---
  const fetchHistories = useCallback(async () => {
  setLoading(true);
  setError(null);
  try {
    const params = {};
    if (pipelineInstId) params.pipelineInstId = pipelineInstId;
    // NEU: Wenn nach Vorgangsname gefiltert, NICHT pipelineInstId setzen!
    if (filterInstance?.value) {
      params.pipelineInstanceName = filterInstance.value; // Name, NICHT ID!
    }
    if (filterStage?.value) params.stageId = filterStage.value;
    if (filterPipeline?.value) params.pipelineDefId = filterPipeline.value;
    if (filterFromDate) params.fromDate = filterFromDate;
    if (filterToDate) params.toDate = filterToDate;
    params.limit = limit;
    params.page = currentPage;
    // Verantwortlicher-Filter und Kundenfilter machen wir unten im Frontend!

    const response = await api.get('/stage-histories', { params });
    const arr = Array.isArray(response.data.data) ? response.data.data : [];
    setHistories(arr);
    setTotalPages(response.data.pages || 1);
  } catch (err) {
    setError('Fehler beim Laden der Phasenwechsel.');
  }
  setLoading(false);
}, [
  pipelineInstId,
  filterInstance,
  filterStage,
  filterPipeline,
  filterFromDate,
  filterToDate,
  currentPage
]);

useEffect(() => {
  fetchHistories();
}, [fetchHistories]);

// Filtert histories im Frontend anhand Kunde und Verantwortlicher
const filteredHistories = histories.filter(h => {
  // Kunde
  if (filterCustomer?.value) {
    const inst = instances.find(i => i.id === h.pipelineInstId);
    const account = inst && (inst.customer || inst.prospect);
    const accountKey = account
      ? `${inst.customer ? 'customer' : 'prospect'}-${account.id}`
      : null;
    if (accountKey !== String(filterCustomer.value)) {
      return false;
    }
  }
  // Verantwortlicher
  if (filterResponsible?.value) {
    const inst = instances.find(i => i.id === h.pipelineInstId);
    if (!(inst && inst.responsible && String(inst.responsible.id) === String(filterResponsible.value))) {
      return false;
    }
  }
  return true;
});

  // --- UI ---
  return (
    <div>
      <Helmet>
        <title>Phasenwechsel / StageHistory</title>
        <meta name="description" content="CRM Phasenwechsel-Log" />
      </Helmet>
      <h2>Phasenwechsel (StageHistory)</h2>

      {/* Filterleiste */}
<div
  style={{
    display: 'flex',
    gap: 14,
    alignItems: 'center',
    marginBottom: 16,
    justifyContent: 'center',
    flexWrap: 'wrap',
  }}
>
  <div style={{ minWidth: 170 }}>
    <Select
      options={instanceOptions}
      value={filterInstance}
      onChange={selected => {
        setFilterInstance(selected);
        setCurrentPage(1);
      }}
      isClearable
      placeholder="Vorgänge"
    />
  </div>
  <div style={{ minWidth: 170 }}>
    <Select
      options={customerOptions}
      value={filterCustomer}
      onChange={selected => {
        setFilterCustomer(selected);
        setCurrentPage(1);
      }}
      isClearable
      placeholder="Kunden"
    />
  </div>
  <div style={{ minWidth: 170 }}>
    <Select
      options={responsibleOptions}
      value={filterResponsible}
      onChange={selected => {
        setFilterResponsible(selected);
        setCurrentPage(1);
      }}
      isClearable
      placeholder="Verantwortliche"
    />
  </div>
  <div style={{ minWidth: 180 }}>
    <Select
      options={stageOptions}
      value={filterStage}
      onChange={selected => {
        setFilterStage(selected);
        setCurrentPage(1);
      }}
      isClearable
      placeholder="Phasen"
    />
  </div>
  <div style={{ minWidth: 170 }}>
    <Select
      options={pipelineOptions}
      value={filterPipeline}
      onChange={selected => {
        setFilterPipeline(selected);
        setCurrentPage(1);
      }}
      isClearable
      placeholder="Pipelines"
    />
  </div>
  <input
    type="date"
    value={filterFromDate}
    onChange={e => {
      setFilterFromDate(e.target.value);
      setCurrentPage(1);
    }}
    style={{ marginTop: 15, maxWidth: 150 }}
    placeholder="Von"
  />
  <input
    type="date"
    value={filterToDate}
    onChange={e => {
      setFilterToDate(e.target.value);
      setCurrentPage(1);
    }}
    style={{ marginTop: 15, maxWidth: 150 }}
    placeholder="Bis"
  />
  <button
    style={{ padding: '5px 12px', background: '#eee', border: '1px solid #bbb', borderRadius: 5 }}
    onClick={() => {
      setFilterInstance(null);
      setFilterCustomer(null);
      setFilterStage(null);
      setFilterPipeline(null);
      setFilterResponsible(null);
      setFilterFromDate('');
      setFilterToDate('');
      setCurrentPage(1);
    }}
  >↩️</button>
</div>

      {/* Tabelle mit Phasenwechsel */}
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Vorgang</th>
            <th>Kunde</th>
            <th>Verantwortlicher</th>
            <th>angelegt</th> 
            <th>zul. bearbeitet</th>
            <th>Pipeline</th>
            <th>Phase</th>
            <th>Eintritt</th>
            <th>Austritt</th>
            <th>Dauer</th>
          </tr>
        </thead>
        <tbody>
          {filteredHistories.length === 0 && (
            <tr>
              <td colSpan={9} style={{ textAlign: 'center', color: '#999', padding: 20 }}>Keine Einträge gefunden.</td>
            </tr>
          )}
          {filteredHistories.map(h => (
            <tr key={h.id}>
              <td>{h.id}</td>
              <td>
                {instances.find(i => i.id === h.pipelineInstId)?.name || `#${h.pipelineInstId}`}
              </td>
              <td>
                {
                  (() => {
                    const inst = instances.find(i => i.id === h.pipelineInstId);
                    if (inst && (inst.customer || inst.prospect)) {
                      const account = inst.customer || inst.prospect;
                      const accountName = account.company || account.name || '-';
                      return inst.prospect ? <em>{accountName}</em> : accountName;
                    }
                    return '-';
                  })()
                }
              </td>
              <td>
                {
                  (() => {
                    const inst = instances.find(i => i.id === h.pipelineInstId);
                    if (inst && inst.responsible) {
                      return `${inst.responsible.firstName || ''} ${inst.responsible.lastName || ''}`.trim() || `#${inst.responsible.id}`;
                    }
                    return '-';
                  })()
                }
              </td>
              <td>
  {
    (() => {
      const inst = instances.find(i => i.id === h.pipelineInstId);
      // User-Objekt aus z.B. inst.createdByUser, oder nur die ID
      if (inst && inst.createdByUser) {
        return `${inst.createdByUser.firstName || ''} ${inst.createdByUser.lastName || ''}`.trim() || `#${inst.createdByUser.id}`;
      } else if (inst && inst.createdBy) {
        // Falls kein User-Objekt geladen, nur die ID anzeigen
        return `#${inst.createdBy}`;
      }
      return '-';
    })()
  }
</td>
<td>
  {
    (() => {
      const inst = instances.find(i => i.id === h.pipelineInstId);
      if (inst && inst.updatedByUser) {
        return `${inst.updatedByUser.firstName || ''} ${inst.updatedByUser.lastName || ''}`.trim() || `#${inst.updatedByUser.id}`;
      } else if (inst && inst.updatedBy) {
        return `#${inst.updatedBy}`;
      }
      return '-';
    })()
  }
</td>
              <td>
                {pipelines.find(p => p.id === h.pipelineDefId)?.name || '-'}
              </td>
              <td>
                {stages.find(s => s.id === h.stageId)?.name || `#${h.stageId}`}
              </td>
              <td>
                {formatDateToGerman(h.enteredAt, true)}
              </td>
              <td>
                {formatDateToGerman(h.leftAt, true)}
              </td>
              <td>
                {formatDuration(h.enteredAt, h.leftAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Pagination */}
      <div style={{ marginTop: '1rem', textAlign: 'center' }}>
  <button
    onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
    disabled={currentPage === 1}
    style={{ fontSize: '15px', padding: '8px 12px', backgroundColor: 'rgb(155, 155, 155)', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
  >
    Vorherige
  </button>
  <span style={{ margin: '0 1rem' }}>
    Seite {currentPage} von {totalPages}
  </span>
  <button
    onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
    disabled={currentPage >= totalPages}
    style={{ fontSize: '15px', padding: '8px 12px', backgroundColor: 'rgb(155, 155, 155)', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
  >
    Nächste
  </button>
      </div>
    </div>
  );
};

export default StageHistoryForm;
